import { Layers, Lock, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FilterChipGridProps } from '@/types/components/audiences/filter-chip-grid'

export function FilterChipGrid({
  items,
  selected,
  onRemove,
  onChipClick,
  onAddClick,
  groups = [],
  onRemoveGroup,
  onGroupClick,
  className,
}: FilterChipGridProps) {
  const selectedSet = new Set(selected)

  // A group collapses into one chip when any of its members is selected.
  // Partial selections (some members in the filter) also collapse so the grid
  // doesn't explode into many near-identical per-member chips.
  const activeGroups = groups.filter(
    (group) =>
      group.memberColumns.length > 0 &&
      group.memberColumns.some((col) => selectedSet.has(col)),
  )
  const groupedColumns = new Set(
    activeGroups.flatMap((group) => group.memberColumns),
  )

  const activeItems = items
    .filter(
      (item) => selectedSet.has(item.sourceColumn) && !groupedColumns.has(item.sourceColumn),
    )
    .sort((a, b) => (a.isLocked === b.isLocked ? 0 : a.isLocked ? -1 : 1))

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
        {activeItems.map((item) => (
          <button
            key={item.sourceColumn}
            type="button"
            className="group flex items-center justify-between rounded-[6px] bg-primary/10 dark:bg-[hsl(var(--segment-option-bg))] px-4 py-3 text-left transition-opacity hover:opacity-80"
            onClick={() => onChipClick(item)}
          >
            <span className="truncate text-base font-medium leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
              {item.displayLabel}
            </span>
            {item.isLocked ? (
              <Lock className="ml-2 h-3.5 w-3.5 shrink-0 text-primary" />
            ) : (
              <span
                role="button"
                aria-label={`Remove ${item.displayLabel}`}
                tabIndex={-1}
                className="ml-2 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove(item.sourceColumn)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation()
                    onRemove(item.sourceColumn)
                  }
                }}
              >
                <X className="h-3.5 w-3.5 text-foreground/60 dark:text-white/60" />
              </span>
            )}
          </button>
        ))}

        {activeGroups.map((group) => (
          <button
            key={group.groupId}
            type="button"
            className="group flex items-center justify-between rounded-[6px] bg-primary/10 dark:bg-[hsl(var(--segment-option-bg))] px-4 py-3 text-left transition-opacity hover:opacity-80"
            onClick={() => onGroupClick?.(group)}
          >
            <span className="flex min-w-0 items-center gap-2">
              <Layers className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
              <span className="truncate text-base font-medium leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
                {group.label}
              </span>
            </span>
            <span
              role="button"
              aria-label={`Remove ${group.label}`}
              tabIndex={-1}
              className="ml-2 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                onRemoveGroup?.(group.groupId)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  onRemoveGroup?.(group.groupId)
                }
              }}
            >
              <X className="h-3.5 w-3.5 text-foreground/60 dark:text-white/60" />
            </span>
          </button>
        ))}

        <button
          type="button"
          className="flex items-center justify-center gap-1.5 rounded-[6px] border-[1.5px] border-dashed border-accent-highlight/40 dark:border-accent-highlight/20 px-4 py-2 text-base font-medium leading-[1.2] tracking-[-0.02em] text-foreground transition-opacity hover:opacity-80 dark:text-white"
          onClick={onAddClick}
        >
          <Plus className="h-3.5 w-3.5 shrink-0" />
          Add filter
        </button>
      </div>
    </div>
  )
}
